import {Box, Stack, Typography, Link, Grid, Button} from "@mui/material";
import { FC } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
    Instagram as InstagramIcon,
    Facebook as FacebookIcon,
    Twitter as TwitterIcon,
} from '@mui/icons-material';

// Import logo
import suntapLogo from '../img/suntap_logo.svg';
import heroIllustration from '../img/hero_illustration.png';
import {PintGlassIcon} from "./Icons";
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';

export const SocialIcons: FC = () => {
    return (
        <Box sx={{ mb: 4 }}>
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} sx={{ mb: 2 }}>
                <Link href="https://www.instagram.com/suntap_app/" target="_blank" rel="noopener noreferrer">
                    <InstagramIcon sx={{ color: 'black', fontSize: '40px' }} />
                </Link>
                {/*<FacebookIcon sx={{ color: 'black', fontSize: '40px' }} />*/}
                {/*<TwitterIcon sx={{ color: 'black', fontSize: '40px' }} />*/}
            </Stack>
            
            {/* City Landing Pages */}
            <Stack direction={'column'}>
                <Typography
                    variant="body2"
                    sx={{
                        fontFamily: "ApfelGrotezkRegular",
                        fontSize: '16px',
                        color: 'black',
                        mb: 1,
                        fontWeight: 'bold'
                    }}
                >
                    Find top 10 sunniest pubs in:
                </Typography>
                <Stack direction={'row'} sx={{ gap: 2, flexWrap: 'wrap' }}>
                    <Link
                        component={RouterLink}
                        to="/sunny-pubs-london"
                        sx={{
                            fontFamily: "ApfelGrotezkRegular",
                            color: 'black',
                            textDecoration: 'none',
                            fontSize: '14px',
                            '&:hover': { textDecoration: 'underline' }
                        }}
                    >
                        London
                    </Link>
                    <Link
                        component={RouterLink}
                        to="/sunny-pubs-bristol"
                        sx={{
                            fontFamily: "ApfelGrotezkRegular",
                            color: 'black',
                            textDecoration: 'none',
                            fontSize: '14px',
                            '&:hover': { textDecoration: 'underline' }
                        }}
                    >
                        Bristol
                    </Link>
                    <Link
                        component={RouterLink}
                        to="/sunny-pubs-brighton-and-hove"
                        sx={{
                            fontFamily: "ApfelGrotezkRegular",
                            color: 'black',
                            textDecoration: 'none',
                            fontSize: '14px',
                            '&:hover': { textDecoration: 'underline' }
                        }}
                    >
                        Brighton & Hove
                    </Link>
                </Stack>
            </Stack>
        </Box>
    );
};

interface FooterProps {
    isLoading?: boolean;
}

export const Footer: FC<FooterProps> = ({ isLoading }) => {
    const currentYear = new Date().getFullYear();
    
    return (
        <Box sx={{ 
            width: '100%', 
            justifyContent: 'left',
            mt: 4,
            pb: 3
        }}>
            
            {/* Suntap Logo */}
            {!isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1, mt: 4 }}>
                    <img
                        src={suntapLogo}
                        alt="Suntap Logo"
                        style={{
                            height: '50px',
                        }}
                    />
                </Box>
            )}



            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 4, mt: 1 }}>
                <img
                    src={heroIllustration}
                    alt="Sunny Garden Illustration"
                    style={{
                        width: '100%',
                    }}
                />
            </Box>

            {/* Buy us a pint */}
            <Box sx={{ mb: 4 }}>
                <Stack
                    direction={'row'}
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        mb: 2
                    }}
                >
                    <PintGlassIcon sx={{ height: '40px' }}/>
                    <Typography
                        variant="h6"
                        sx={{
                            fontFamily: "ApfelGrotezkBold",
                            fontSize: '30px',
                            color: 'black',
                            ml: 2,
                        }}
                    >
                        Cheeky half pint?
                    </Typography>
                </Stack>
                <Typography
                    variant="body2"
                    sx={{
                        fontFamily: "ApfelGrotezkRegular",
                        fontSize: '20px',
                        color: 'black',
                        mb: 2
                    }}
                >
                    If you love Suntap, consider buying us a half to keep the bar propped up.
                </Typography>
                
                {/* PayPal.Me Link */}
                <Button
                    sx={{
                        width: '100%',
                        background: 'transparent',
                        color: '#000',
                        fontSize: '1.1rem',
                        fontFamily: "ApfelGrotezkRegular",
                        borderRadius: '48px',
                        transition: 'all 0.2s ease',
                        border: `2px solid #000`,
                    }}
                    endIcon={<VerifiedUserOutlinedIcon />}
                    onClick={() => window.open('https://paypal.me/suntaplondon/3.50?country.x=GB&locale.x=en_GB', '_blank')}
                >
                    Buy us a half pint (£3.50)
                </Button>

            </Box>
            
            {/* Quick Links */}
            <Stack direction={'column'}>
                <Link
                    component={RouterLink}
                    to="/about"
                    sx={{
                        fontFamily: "ApfelGrotezkRegular",
                        color: 'black',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                        mb: 1,
                        fontSize: '20px',
                    }}
                >
                    About & FAQ
                </Link>
                <Link
                    component={RouterLink}
                    to="/privacy-policy"
                    sx={{
                        fontFamily: "ApfelGrotezkRegular",
                        color: 'black',
                        textDecoration: 'none',
                        mb: 1,
                        fontSize: '20px',
                        '&:hover': { textDecoration: 'underline' }
                    }}
                >
                    Privacy Policy
                </Link>
                <Link
                    component={RouterLink}
                    to="/terms-of-use"
                    sx={{
                        fontFamily: "ApfelGrotezkRegular",
                        color: 'black',
                        textDecoration: 'none',
                        mb: 1,
                        fontSize: '20px',
                        '&:hover': { textDecoration: 'underline' }
                    }}
                >
                    Terms of Use
                </Link>
                <Link
                    href="mailto:hello@sun-tap.com"
                    sx={{
                        fontFamily: "ApfelGrotezkRegular",
                        color: 'black',
                        textDecoration: 'none',
                        fontSize: '20px',
                        mb: 3,
                        '&:hover': { textDecoration: 'underline' }
                    }}
                >
                    hello@sun-tap.com
                </Link>
            </Stack>

            <SocialIcons />

            {/* Copyright */}
            <Typography
                variant="body2"
                sx={{
                    fontFamily: "ApfelGrotezkRegular",
                    fontSize: '22px',
                    mb: 4,
                    color: 'black'
                }}
            >
                © {currentYear} Suntap. All rights reserved.
            </Typography>

            {/* Google Maps Attribution Section - Required by Google ToS */}
            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <img
                        src={require("../img/google_logo/desktop/google_on_white.png")}
                        alt="Google"
                        height="24"
                        width="auto"
                    />
                    <Typography variant="body2" sx={{ fontFamily: "Roboto, sans-serif", fontSize: '14px' }}>
                        Map data ©{currentYear} Google
                    </Typography>
                </Box>

                <Typography variant="body2" sx={{ fontFamily: "Roboto, sans-serif", fontSize: '14px', mb: 1 }}>
                    This application includes Google Maps features and content. Use of Google Maps features and content is subject to the 
                    <Link
                        href="https://maps.google.com/help/terms_maps/"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: 'black', textDecoration: 'underline' }}
                    >
                        Google Maps/Google Earth Additional Terms of Service
                    </Link>
                     and the 
                    <Link
                        href="https://policies.google.com/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: 'black', textDecoration: 'underline' }}
                    >
                        Google Privacy Policy
                    </Link>.
                </Typography>
            </Box>
        </Box>
    );
};

export default Footer;